import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./FixedCostsPage.css";
import SuccessPopup from "./SuccessPopup";
import ErrorPopup from "./ErrorPopup";

const FixedCostsPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [costName, setCostName] = useState("");
  const [costType, setCostType] = useState("amount");
  const [amount, setAmount] = useState("");
  const [percentage, setPercentage] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const resetForm = () => {
    setCostName("");
    setCostType("amount");
    setAmount("");
    setPercentage("");
    setEditingId(null);
  };

  const showSuccess = (message) => {
    setPopupMessage(message);
    setShowErrorPopup(false);
    setShowSuccessPopup(true);
  };

  const showError = (message) => {
    setPopupMessage(message);
    setShowSuccessPopup(false);
    setShowErrorPopup(true);
  };

  const fetchFixedCosts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/fixed-costs/${businessId}`);
      setCosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch fixed costs:", err);
      showError(err?.response?.data?.message || "Could not load fixed costs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFixedCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        cost_name: costName,
        cost_type: costType,
      };

      if (costType === "amount") {
        payload.amount = amount;
        payload.percentage = null;
      }

      if (costType === "percentage") {
        payload.percentage = percentage;
        payload.amount = null;
      }

      const isEditing = Boolean(editingId);

      if (isEditing) {
        await api.put(`/fixed-costs/${businessId}/${editingId}`, payload);
      } else {
        await api.post(`/fixed-costs/${businessId}`, payload);
      }

      resetForm();
      await fetchFixedCosts();
      showSuccess(
        isEditing
          ? "Fixed cost updated successfully."
          : "Fixed cost added successfully."
      );
    } catch (err) {
      console.error("Failed to save fixed cost:", err);
      showError(err?.response?.data?.message || "Could not save fixed cost.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cost) => {
    setEditingId(cost.id);
    setCostName(cost.cost_name || "");
    setCostType(cost.cost_type || "amount");
    setAmount(
      cost.cost_type === "amount" && cost.amount != null
        ? String(cost.amount)
        : ""
    );
    setPercentage(
      cost.cost_type === "percentage" && cost.percentage != null
        ? String(cost.percentage)
        : ""
    );
  };

  const handleDelete = async (costId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this fixed cost?"
    );
    if (!confirmed) return;

    try {
      setDeletingId(costId);
      await api.delete(`/fixed-costs/${businessId}/${costId}`);

      if (editingId === costId) {
        resetForm();
      }

      await fetchFixedCosts();
      showSuccess("Fixed cost deleted successfully.");
    } catch (err) {
      console.error("Failed to delete fixed cost:", err);
      showError(err?.response?.data?.message || "Could not delete fixed cost.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed-costs-container">
      <h1 className="pw-page-title">Fixed Costs</h1>

      <div className="fixed-costs-topbar">
        <button
          type="button"
          className="fixed-costs-btn"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      <div className="fixed-costs-card">
        <h2>{editingId ? "Edit Fixed Cost" : "Add Fixed Cost"}</h2>

        <form className="fixed-costs-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Cost Name"
            value={costName}
            onChange={(e) => setCostName(e.target.value)}
            required
          />

          <select
            value={costType}
            onChange={(e) => {
              const newType = e.target.value;
              setCostType(newType);

              if (newType === "amount") {
                setPercentage("");
              } else {
                setAmount("");
              }
            }}
          >
            <option value="amount">Amount Per Month</option>
            <option value="percentage">Percentage Of Sales</option>
          </select>

          {costType === "amount" ? (
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Monthly Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          ) : (
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Percentage"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              required
            />
          )}

          <div className="fixed-costs-form-actions">
            <button
              type="submit"
              className="fixed-costs-btn primary"
              disabled={saving}
            >
              {saving ? "Saving..." : editingId ? "Update Cost" : "Save Cost"}
            </button>

            {editingId ? (
              <button
                type="button"
                className="fixed-costs-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="fixed-costs-card">
        <h2>Saved Fixed Costs</h2>

        {loading ? (
          <div className="fixed-costs-empty">Loading...</div>
        ) : costs.length === 0 ? (
          <div className="fixed-costs-empty">No fixed costs found.</div>
        ) : (
          <div className="fixed-costs-table-wrap">
            <table className="fixed-costs-table">
              <thead>
                <tr>
                  <th>Cost Name</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Percentage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((cost) => (
                  <tr key={cost.id}>
                    <td>{cost.cost_name}</td>
                    <td>
                      {cost.cost_type === "amount"
                        ? "Amount Per Month"
                        : "Percentage Of Sales"}
                    </td>
                    <td>
                      {cost.cost_type === "amount" && cost.amount != null
                        ? formatMoney(cost.amount)
                        : "—"}
                    </td>
                    <td>
                      {cost.cost_type === "percentage" &&
                      cost.percentage != null
                        ? `${parseFloat(cost.percentage).toFixed(2)}%`
                        : "—"}
                    </td>
                    <td>
                      <div className="fixed-costs-row-actions">
                        <button
                          type="button"
                          className="fixed-costs-btn"
                          onClick={() => handleEdit(cost)}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="fixed-costs-btn danger"
                          onClick={() => handleDelete(cost.id)}
                          disabled={deletingId === cost.id}
                        >
                          {deletingId === cost.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSuccessPopup && (
        <SuccessPopup
          title="Success"
          message={popupMessage}
          onClose={() => setShowSuccessPopup(false)}
          buttonLabel="Done"
        />
      )}

      {showErrorPopup && (
        <ErrorPopup
          title="Error"
          message={popupMessage}
          onClose={() => setShowErrorPopup(false)}
          buttonLabel="Close"
        />
      )}
    </div>
  );
};

export default FixedCostsPage;

